import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Form, Spinner } from 'react-bootstrap';

const createSessionToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `nn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Street-address field with Google Places Autocomplete (via our Express proxy).
 * Manual typing always works; suggestions are optional when the API is configured.
 */
class AddressAutocomplete extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      enabled: false,
      suggestions: [],
      open: false,
      loading: false,
      selecting: false,
      error: null,
      activeIndex: -1,
    };
    this.sessionToken = createSessionToken();
    this.debounceTimer = null;
    this.requestId = 0;
    this.rootRef = React.createRef();
    this.listId = 'nn-address-suggestions';
  }

  componentDidMount() {
    this.checkEnabled();
    document.addEventListener('mousedown', this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentClick);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  checkEnabled = async () => {
    try {
      const res = await axios.get('/api/places/status');
      this.setState({ enabled: Boolean(res.data && res.data.enabled) });
    } catch (err) {
      this.setState({ enabled: false });
    }
  };

  handleDocumentClick = (event) => {
    if (
      this.rootRef.current &&
      !this.rootRef.current.contains(event.target)
    ) {
      this.setState({ open: false, activeIndex: -1 });
    }
  };

  handleChange = (event) => {
    const { value } = event.target;
    this.props.onChange(value);

    if (!this.state.enabled) return;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      this.setState({
        suggestions: [],
        open: false,
        loading: false,
        error: null,
        activeIndex: -1,
      });
      return;
    }

    this.setState({ loading: true, error: null });
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(trimmed);
    }, 300);
  };

  fetchSuggestions = async (input) => {
    const requestId = ++this.requestId;
    try {
      const res = await axios.post('/api/places/autocomplete', {
        input,
        sessionToken: this.sessionToken,
      });
      if (requestId !== this.requestId) return;
      const suggestions = (res.data && res.data.suggestions) || [];
      this.setState({
        suggestions,
        open: suggestions.length > 0,
        loading: false,
        activeIndex: suggestions.length ? 0 : -1,
        error: null,
      });
    } catch (err) {
      if (requestId !== this.requestId) return;
      // Graceful degradation — manual entry still works.
      this.setState({
        suggestions: [],
        open: false,
        loading: false,
        activeIndex: -1,
        error: null,
      });
    }
  };

  applySuggestion = async (suggestion) => {
    if (!suggestion || !suggestion.placeId || this.state.selecting) return;
    this.setState({ selecting: true, open: false, loading: true });
    try {
      const res = await axios.post('/api/places/details', {
        placeId: suggestion.placeId,
        sessionToken: this.sessionToken,
      });
      const address = (res.data && res.data.address) || {};
      if (this.props.onSelectAddress) {
        this.props.onSelectAddress(address);
      }
      // End billing session; next typing starts a new one.
      this.sessionToken = createSessionToken();
      this.setState({
        suggestions: [],
        selecting: false,
        loading: false,
        activeIndex: -1,
        error: null,
      });
    } catch (err) {
      this.setState({
        selecting: false,
        loading: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          'Could not fill that address. You can enter it manually.',
      });
    }
  };

  handleKeyDown = (event) => {
    const { open, suggestions, activeIndex } = this.state;
    if (!open || !suggestions.length) {
      if (event.key === 'Escape') {
        this.setState({ open: false, activeIndex: -1 });
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({
        activeIndex: (activeIndex + 1) % suggestions.length,
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setState({
        activeIndex:
          activeIndex <= 0 ? suggestions.length - 1 : activeIndex - 1,
      });
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      this.applySuggestion(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.setState({ open: false, activeIndex: -1 });
    }
  };

  render() {
    const { value, id, name, disabled } = this.props;
    const {
      enabled,
      suggestions,
      open,
      loading,
      selecting,
      error,
      activeIndex,
    } = this.state;

    return (
      <div className="address-autocomplete" ref={this.rootRef}>
        <Form.Label htmlFor={id || 'shipping-address-line1'}>Address</Form.Label>
        <div className="address-autocomplete__input-wrap">
          <Form.Control
            id={id || 'shipping-address-line1'}
            name={name || 'addressLine1'}
            value={value}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onFocus={() => {
              if (suggestions.length) this.setState({ open: true });
            }}
            autoComplete="address-line1"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={this.listId}
            aria-activedescendant={
              open && activeIndex >= 0
                ? `${this.listId}-option-${activeIndex}`
                : undefined
            }
            disabled={disabled || selecting}
            required
            placeholder={
              enabled ? 'Start typing your street address' : undefined
            }
          />
          {(loading || selecting) && (
            <Spinner
              animation="border"
              size="sm"
              className="address-autocomplete__spinner"
              role="status"
              aria-label="Loading address suggestions"
            />
          )}
        </div>
        {error && (
          <div className="address-autocomplete__error" role="alert">
            {error}
          </div>
        )}
        {open && suggestions.length > 0 && (
          <ul
            id={this.listId}
            className="address-autocomplete__list"
            role="listbox"
            aria-label="Address suggestions"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.placeId}
                id={`${this.listId}-option-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex
                    ? 'address-autocomplete__option is-active'
                    : 'address-autocomplete__option'
                }
                onMouseDown={(event) => {
                  // Prevent input blur before click applies.
                  event.preventDefault();
                }}
                onClick={() => this.applySuggestion(suggestion)}
                onMouseEnter={() => this.setState({ activeIndex: index })}
              >
                <span className="address-autocomplete__main">
                  {suggestion.mainText || suggestion.description}
                </span>
                {suggestion.secondaryText && (
                  <span className="address-autocomplete__secondary">
                    {suggestion.secondaryText}
                  </span>
                )}
              </li>
            ))}
            <li className="address-autocomplete__attribution" aria-hidden="true">
              Powered by Google
            </li>
          </ul>
        )}
        {enabled && (
          <Form.Text className="text-muted">
            Suggestions appear as you type. You can also enter an address
            manually.
          </Form.Text>
        )}
      </div>
    );
  }
}

AddressAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSelectAddress: PropTypes.func.isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
};

AddressAutocomplete.defaultProps = {
  value: '',
  id: 'shipping-address-line1',
  name: 'addressLine1',
  disabled: false,
};

export default AddressAutocomplete;
